import { useEffect, useMemo, useState } from 'react'
import { collection, documentId, query, where } from 'firebase/firestore'
import { db, isOtaBoot } from '../lib/firebase'
import { getDocs, getDocsCacheFirst } from '../lib/firestore'
import { chunk, FIRESTORE_IN_LIMIT } from '../lib/chunk'
import { log } from '../lib/logger'
import { loadVenues, type VenueFetchTimes } from '../lib/venueFetch'
import type { Venue } from '../types'

export const VENUE_FETCH_TIMES_KEY = 'venues:fetchedAt'

// Survive unmount so navigating between pages doesn't re-read a venue.
const venueMemCache = new Map<string, Venue>()
// Ids already loaded or in flight this session, so a venue that genuinely
// doesn't exist (deleted, or an event pointing at a bad id) can't put a page
// into a refetch loop.
const requested = new Set<string>()
// Ids whose load has settled, found or not — `loading` waits only on the rest.
const settled = new Set<string>()
// Every mounted instance re-renders when any load settles: one that found its
// ids already requested by another instance would otherwise wait forever.
const listeners = new Set<() => void>()

/**
 * The venues with the given ids, keyed by id, on a per-venue TTL: each is served
 * from the persistent cache while its last network read is under a day old,
 * re-read from the network after that (see lib/venueFetch.ts).
 *
 * Used to derive the region of an event that carries no stamped `regionId`.
 * Published events are stamped by trigger, so most pages need none and read
 * nothing; this used to re-read the whole collection (~800 docs) daily per
 * device.
 */
export function useVenues(requiredIds: string[] = []): {
  venues: Map<string, Venue>
  loading: boolean
} {
  const key = [...new Set(requiredIds)].sort().join(',')
  const [version, setVersion] = useState(0)

  useEffect(() => {
    const bump = () => setVersion(v => v + 1)
    listeners.add(bump)
    return () => {
      listeners.delete(bump)
    }
  }, [])

  useEffect(() => {
    const missing = key ? key.split(',').filter(id => !requested.has(id)) : []
    if (missing.length === 0) return
    missing.forEach(id => requested.add(id))

    // A failed load (network down) settles too: region grouping is an
    // enhancement, never a gate on the page.
    loadVenues(missing, readFetchTimes(), Date.now(), { readCache, readNetwork })
      .then(({ venues, fetchedFromNetwork }) => {
        venues.forEach((venue, id) => venueMemCache.set(id, venue))
        // Skipped on OTA boots: Firestore runs memory-only there, so nothing
        // reached IndexedDB and the next cold start must re-fetch.
        if (!isOtaBoot) writeFetchTimes(fetchedFromNetwork, Date.now())
      })
      .catch(err => log('venues', 'load failed', { err: String(err) }))
      .finally(() => {
        missing.forEach(id => settled.add(id))
        listeners.forEach(notify => notify())
      })
  }, [key])

  const venues = useMemo(() => {
    void version
    const map = new Map<string, Venue>()
    for (const id of key ? key.split(',') : []) {
      const venue = venueMemCache.get(id)
      if (venue) map.set(id, venue)
    }
    return map
  }, [key, version])
  const waiting = key !== '' && key.split(',').some(id => !venueMemCache.has(id) && !settled.has(id))
  return { venues, loading: waiting && venues.size === 0 }
}

const byIds = (ids: string[]) => query(collection(db, 'venues'), where(documentId(), 'in', ids))

async function readChunked(ids: string[], read: typeof getDocs): Promise<Map<string, Venue>> {
  const snaps = await Promise.all(chunk(ids, FIRESTORE_IN_LIMIT).map(c => read(byIds(c))))
  return new Map(snaps.flatMap(snap => snap.docs.map(d => [d.id, { ...d.data(), id: d.id } as Venue] as const)))
}

const readCache = (ids: string[]) => readChunked(ids, getDocsCacheFirst)
const readNetwork = (ids: string[]) => readChunked(ids, getDocs)

function readFetchTimes(): VenueFetchTimes {
  try {
    return JSON.parse(localStorage.getItem(VENUE_FETCH_TIMES_KEY) ?? '{}') as VenueFetchTimes
  } catch {
    return {}
  }
}

function writeFetchTimes(ids: string[], now: number): void {
  if (ids.length === 0) return
  const times = readFetchTimes()
  for (const id of ids) times[id] = now
  try {
    localStorage.setItem(VENUE_FETCH_TIMES_KEY, JSON.stringify(times))
  } catch {
    // Storage full or blocked: these venues just re-read from the network next load.
  }
}
